import React, { useState, useRef, useEffect } from 'react';
import { XMarkIcon } from '@heroicons/react/24/outline';
import { exportAPI } from '../../services';
import { useAuth } from '../../contexts/AuthContext';
import { STORAGE_KEYS } from '../../constants';
import DateRangePicker from './DateRangePicker';
import NicoleToast from './NicoleToast';

const DATASET_OPTIONS = [
  { key: 'owners', label: 'Owners' },
  { key: 'pets', label: 'Pets' },
];

function ExportDataModal({ isOpen, onClose }) {
  const { getOrganizationType, activeOrganizationId } = useAuth();
  const organizationType = getOrganizationType();
  const isGovernmentOrg = organizationType && organizationType.toLowerCase() === 'government';

  const [selectedDatasets, setSelectedDatasets] = useState({ owners: true, pets: true });
  const [showDatasetDropdown, setShowDatasetDropdown] = useState(false);
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [fileFormat, setFileFormat] = useState('csv');
  const [exporting, setExporting] = useState(false);
  const modalRef = useRef(null);
  const dropdownRef = useRef(null);

  useEffect(() => {
    if (!isOpen) {
      setSelectedDatasets({ owners: true, pets: true });
      setShowDatasetDropdown(false);
      setStartDate('');
      setEndDate('');
      setFileFormat('csv');
      setExporting(false);
    }
  }, [isOpen]);

  useEffect(() => {
    const handleEscape = (e) => {
      if (e.key === 'Escape' && isOpen) onClose();
    };
    document.addEventListener('keydown', handleEscape);
    return () => document.removeEventListener('keydown', handleEscape);
  }, [isOpen, onClose]);

  useEffect(() => {
    const handler = (e) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target)) {
        setShowDatasetDropdown(false);
      }
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleBackdropClick = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  const toggleDataset = (key) => {
    setSelectedDatasets(prev => ({ ...prev, [key]: !prev[key] }));
  };

  const selectedCount = Object.values(selectedDatasets).filter(Boolean).length;

  const datasetLabel = (() => {
    const active = DATASET_OPTIONS.filter(o => selectedDatasets[o.key]);
    if (active.length === 0) return 'Select dataset';
    return active.map(o => o.label).join(', ');
  })();

  const [downloadStarted, setDownloadStarted] = useState(false);

  // Pull the server-suggested filename out of the Content-Disposition header,
  // falling back to a sensible default when it isn't present.
  const filenameFromResponse = (res, fallback) => {
    const cd = res?.headers?.['content-disposition'] || '';
    const match = /filename\*?=(?:UTF-8'')?["']?([^"';]+)["']?/i.exec(cd);
    return match ? decodeURIComponent(match[1]) : fallback;
  };

  // Hand the freshly-built export blob straight to the browser as a download.
  const triggerBrowserDownload = (blob, filename) => {
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    URL.revokeObjectURL(url);
  };

  const handleExport = async () => {
    if (selectedCount === 0 || !startDate || !endDate) return;
    setExporting(true);
    setDownloadStarted(false);

    try {
      const effOrgType = organizationType || localStorage.getItem(STORAGE_KEYS.ORGANIZATION_TYPE);
      const effIsGov = effOrgType && effOrgType.toLowerCase() === 'government';

      const sheets = DATASET_OPTIONS.filter(o => selectedDatasets[o.key]).map(o => o.key);

      // Scope strictly to the active organization. Send the id explicitly so the
      // export never depends on the interceptor's implicit injection.
      const activeOrgId = activeOrganizationId
        || localStorage.getItem(STORAGE_KEYS.ORGANIZATION_ACTIVE_ID)
        || null;

      const params = {};
      if (activeOrgId) params.organization_ids = String(activeOrgId);
      if (startDate) params.start_date = startDate;
      if (endDate) params.end_date = endDate;
      if (effIsGov) params.source = 'combined';
      params.dataset = sheets.join(',');
      params.file_format = fileFormat;

      // Export Data is a direct download: the backend streams the file back and
      // nothing is emailed.
      const res = await exportAPI.downloadRaw(params);

      const single = sheets.length === 1 ? sheets[0] : null;
      const ext = fileFormat === 'csv' ? (single ? 'csv' : 'zip') : 'xlsx';
      const base = single ? `petpulse-${single}-export` : 'petpulse-export';
      const fallback = `${base}.${ext}`;
      const filename = filenameFromResponse(res, fallback);

      triggerBrowserDownload(res.data, filename);

      setDownloadStarted(true);
      onClose();
    } catch (err) {
      console.error('Export failed:', err);
      alert('Failed to export data. Please try again.');
    } finally {
      setExporting(false);
    }
  };

  return (
    <>
      <NicoleToast
        visible={downloadStarted}
        message="Nicole prepared your export — your download is starting"
        onClose={() => setDownloadStarted(false)}
      />

      {isOpen && (
        <div
          className="fixed inset-0 z-[100] flex items-center justify-center bg-black/40 backdrop-blur-sm"
          onClick={handleBackdropClick}
        >
          <div
            ref={modalRef}
            className="relative w-full max-w-sm mx-4 bg-white rounded-2xl shadow-2xl p-6"
          >
            {/* Close button */}
            <button
              onClick={onClose}
              className="absolute top-4 right-4 p-1 rounded-full text-gray-400 hover:text-gray-600 hover:bg-gray-100 transition-colors"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>

            {/* Title */}
            <h2 className="text-xl font-bold text-[#2492B6] mb-5">Export Data</h2>

            {/* Dataset — custom multi-select dropdown */}
            <div className="relative mb-5" ref={dropdownRef}>
              <label className="block text-sm font-medium text-gray-700 mb-1.5">Dataset</label>
              <button
                type="button"
                onClick={() => setShowDatasetDropdown(prev => !prev)}
                className="w-full flex items-center justify-between px-4 py-2.5 rounded-xl border border-gray-300 bg-white text-sm text-left focus:outline-none focus:ring-2 focus:ring-cyan-500/30 focus:border-cyan-500 transition-colors"
              >
                <span className={selectedCount === 0 ? 'text-gray-400' : 'text-gray-700'}>
                  {datasetLabel}
                </span>
                <svg className={`h-4 w-4 text-gray-400 transition-transform ${showDatasetDropdown ? 'rotate-180' : ''}`} fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
                  <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                </svg>
              </button>

              {showDatasetDropdown && (
                <div className="absolute left-0 right-0 top-full mt-1 bg-white rounded-xl border border-gray-200 shadow-lg overflow-hidden z-10">
                  {DATASET_OPTIONS.map((option) => (
                    <label
                      key={option.key}
                      className="flex items-center gap-3 px-4 py-3 hover:bg-gray-50 cursor-pointer transition-colors"
                      onClick={(e) => { e.preventDefault(); toggleDataset(option.key); }}
                    >
                      <div
                        className={`w-5 h-5 rounded flex-shrink-0 flex items-center justify-center transition-colors ${
                          selectedDatasets[option.key]
                            ? 'bg-[#2492B6]'
                            : 'border-2 border-gray-300 bg-white'
                        }`}
                      >
                        {selectedDatasets[option.key] && (
                          <svg className="w-3 h-3 text-white" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={3.5}>
                            <path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" />
                          </svg>
                        )}
                      </div>
                      <span className="text-sm font-medium text-gray-800">{option.label}</span>
                    </label>
                  ))}
                </div>
              )}
            </div>

            {/* Date Range */}
            <div className="mb-5">
              <DateRangePicker
                startDate={startDate}
                endDate={endDate}
                onStartChange={setStartDate}
                onEndChange={setEndDate}
              />
            </div>

            {/* File Format */}
            <div className="mb-6">
              <label className="block text-sm font-medium text-gray-700 mb-1.5">File format</label>
              <div className="flex gap-2">
                <button
                  onClick={() => setFileFormat('csv')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                    fileFormat === 'csv'
                      ? 'text-[#2492B6] border-[#2492B6] bg-cyan-50'
                      : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  .CSV
                </button>
                <button
                  onClick={() => setFileFormat('xlsx')}
                  className={`px-5 py-2 rounded-full text-sm font-semibold border transition-all duration-200 ${
                    fileFormat === 'xlsx'
                      ? 'text-[#2492B6] border-[#2492B6] bg-cyan-50'
                      : 'bg-white text-gray-500 border-gray-300 hover:border-gray-400'
                  }`}
                >
                  .XLSX
                </button>
              </div>
            </div>

            {/* Export Button */}
            <button
              onClick={handleExport}
              disabled={exporting || selectedCount === 0 || !startDate || !endDate}
              className={`w-full flex items-center justify-center gap-2 py-3 rounded-full text-sm font-semibold border transition-all duration-200 ${
                exporting || selectedCount === 0 || !startDate || !endDate
                  ? 'bg-gray-50 text-gray-400 border-gray-200 cursor-not-allowed'
                  : 'bg-white text-[#2492B6] border-[#2492B6]/40 hover:bg-cyan-50 hover:border-[#2492B6] hover:shadow-sm'
              }`}
            >
              <img src="https://cdn.pawnec.com/petpulse/Vetscribe%20AI%20-%2003%20Clinic%20Data%20Analyst%20Avatar.gif" alt="Nicole AI" className="h-6 w-6 rounded-full object-cover" />
              {exporting ? 'Exporting...' : 'Export data with Nicole'}
            </button>
          </div>
        </div>
      )}
    </>
  );
}

export default ExportDataModal;
